import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pnidApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { PnidWizardState } from '../types';

/** A saved review snapshot, refreshed deliberately after local annotations change. */
export function usePublishableAssets(
  docId: string,
  state: Pick<PnidWizardState, 'equipments' | 'instruments'> | null,
  enabled: boolean,
) {
  const changes = useRef({ docId, equipments: state?.equipments, instruments: state?.instruments,
    generation: 0, started: false });
  const [accepted, setAccepted] = useState<{ docId: string; generation: number } | null>(null);
  if (changes.current.docId !== docId) {
    setAccepted(null);
    changes.current = { docId, equipments: state?.equipments, instruments: state?.instruments,
      generation: 0, started: false };
  } else if (state && (changes.current.equipments !== state.equipments
    || changes.current.instruments !== state.instruments)) {
    changes.current = { ...changes.current, equipments: state.equipments,
      instruments: state.instruments, generation: changes.current.generation + 1 };
  }

  const query = useQuery({
    queryKey: queryKeys.publishableAssets(docId),
    queryFn: ({ signal }) => pnidApi.getPublishableAssets(docId, signal),
    enabled: false,
    staleTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const { refetch } = query;
  const refresh = useCallback(async () => {
    const generation = changes.current.generation;
    const result = await refetch();
    if (result.isSuccess && changes.current.docId === docId) {
      setAccepted({ docId, generation });
    }
  }, [docId, refetch]);

  useEffect(() => {
    if (!enabled || !state || changes.current.started) return;
    changes.current.started = true;
    void refresh();
  }, [enabled, state, refresh]);

  const hasSnapshot = accepted?.docId === docId;
  return {
    assets: hasSnapshot ? query.data : undefined,
    stale: hasSnapshot && accepted.generation !== changes.current.generation,
    loading: query.isFetching || (!hasSnapshot && !query.isError),
    error: query.isError ? 'The list could not be loaded. Please try again.' : null,
    refresh,
  };
}
